import type { FormInstance } from '@arco-design/web-react/es/Form';
import { get } from 'lodash';

import type { MaasGroupSpec } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { MaasModelSourceType } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { ensurePrimaryGroupSpec, getPrimaryGroupField } from '@/delivery-type-plugin/capabilities/maas-node-groups/masterGroupForm';
import { safeParseJSON } from '@/utils/json-parse';

/** 容器实例表单格式的资源建议值 */
export interface ModelResourceSuggestion {
  cpu?: { request: number; limit?: number };
  memory?: { request: number; limit?: number };
  gpu?: { number?: number };
}

function toPositiveNumber(value: unknown): number | undefined {
  const num = Number(value);
  if (Number.isNaN(num) || num <= 0) return undefined;
  return Math.floor(num);
}

/** 从 config.json 解析资源建议（兼容多种常见字段命名）；解析失败返回 null，不抛错 */
function parseModelConfigResource(config: unknown): ModelResourceSuggestion | null {
  try {
    if (!config || typeof config !== 'object') return null;

    const root = config as Record<string, unknown>;
    const resource =
      (root.resource as Record<string, unknown> | undefined) ||
      (root.resources as Record<string, unknown> | undefined) ||
      (root.recommended_resources as Record<string, unknown> | undefined) ||
      (root.recommendedResources as Record<string, unknown> | undefined);

    const cpuRaw = resource?.cpu ?? resource?.cpu_cores ?? resource?.cpuCores ?? root.cpu ?? root.cpu_cores ?? root.cpuCores;
    const memRaw =
      resource?.memory ??
      resource?.memory_gb ??
      resource?.memoryGi ??
      resource?.memory_gi ??
      root.memory ??
      root.memory_gb ??
      root.memoryGi;
    const gpuRaw = resource?.gpu ?? resource?.gpu_count ?? resource?.gpuCount ?? root.gpu ?? root.gpu_count;

    const cpu = toPositiveNumber(cpuRaw);
    const memory = toPositiveNumber(memRaw);
    const gpu = toPositiveNumber(gpuRaw);

    if (!cpu && !memory && !gpu) return null;

    const suggestion: ModelResourceSuggestion = {};
    if (cpu) suggestion.cpu = { request: cpu, limit: cpu };
    if (memory) suggestion.memory = { request: memory, limit: memory };
    if (gpu) suggestion.gpu = { number: gpu };

    return suggestion;
  } catch {
    return null;
  }
}

/** 拉取模型仓库 config.json；任意失败均静默返回 null */
async function fetchHuggingFaceConfigJson(modelId: string): Promise<unknown | null> {
  try {
    const url = `https://huggingface.co/${modelId}/resolve/main/config.json`;
    const res = await fetch(url, { method: 'GET' });
    if (!res.ok) return null;
    const text = await res.text();
    return safeParseJSON(text, null);
  } catch {
    return null;
  }
}

/** 经本地代理拉取 ModelScope config.json */
async function fetchModelScopeConfigJson(modelId: string): Promise<unknown | null> {
  try {
    const params = new URLSearchParams({ Revision: 'master', FilePath: 'config.json' });
    const url = `/modelscope-api/v1/models/${encodeURIComponent(modelId)}/repo?${params.toString()}`;
    const res = await fetch(url, { method: 'GET' });
    if (!res.ok) return null;

    const body = (await res.json()) as Record<string, unknown>;
    const code = body?.Code ?? body?.code;
    if (code !== undefined && code !== 200 && code !== '200') return null;

    const data = (body?.Data ?? body?.data) as Record<string, unknown> | undefined;
    if (data?.Content && typeof data.Content === 'string') {
      return safeParseJSON(data.Content, null);
    }

    if (body && typeof body === 'object' && parseModelConfigResource(body)) {
      return body;
    }
  } catch {
    return null;
  }

  return null;
}

/** 拉取模型仓库 config.json（失败时静默返回 null） */
async function fetchModelConfigJson(sourceType: MaasModelSourceType, modelId: string): Promise<unknown | null> {
  const id = String(modelId || '').trim();
  if (!id) return null;

  try {
    if (sourceType === MaasModelSourceType.HuggingFace) {
      return await fetchHuggingFaceConfigJson(id);
    }
    if (sourceType === MaasModelSourceType.ModelScope) {
      return await fetchModelScopeConfigJson(id);
    }
  } catch {
    return null;
  }

  return null;
}

/** Master 组资源是否已有用户填写 */
function hasMasterResourceValues(form: FormInstance): boolean {
  const groupField = getPrimaryGroupField(form.getFieldValue('runMode'));
  const resource = get(form.getFieldValue(groupField), 'resource') as Record<string, unknown> | undefined;
  const cpu = get(resource, 'cpu.request');
  const memory = get(resource, 'memory.request');
  return (cpu !== undefined && cpu !== null && cpu !== '') || (memory !== undefined && memory !== null && memory !== '');
}

/** 将 config.json 资源建议写入 Master 组（仅当尚未配置 CPU/内存时） */
function applyModelResourceSuggestionToForm(form: FormInstance, suggestion: ModelResourceSuggestion | null) {
  if (!suggestion || hasMasterResourceValues(form)) return;

  try {
    ensurePrimaryGroupSpec(form);
    const groupField = getPrimaryGroupField(form.getFieldValue('runMode'));
    const master = form.getFieldValue(groupField) as MaasGroupSpec;
    const resource = (master.resource || {}) as Record<string, unknown>;
    form.setFieldValue(groupField, {
      ...master,
      resource: {
        ...resource,
        ...(suggestion.cpu ? { cpu: suggestion.cpu } : {}),
        ...(suggestion.memory ? { memory: suggestion.memory } : {}),
        ...(suggestion.gpu?.number
          ? {
              gpu: {
                ...(resource.gpu as Record<string, unknown> | undefined),
                number: suggestion.gpu.number,
              },
            }
          : {}),
      },
    });
  } catch {
    // 写入表单失败时静默忽略
  }
}

/**
 * 按模型来源拉取 config.json 并尝试初始化 Master 资源建议值。
 * 能解析则写入；拉取失败、JSON 无效或无资源字段时不提示、不抛错。
 */
export async function fetchAndApplyModelConfigResource(
  form: FormInstance,
  sourceType: MaasModelSourceType,
  modelId: string,
): Promise<void> {
  try {
    const config = await fetchModelConfigJson(sourceType, modelId);
    if (!config) return;
    const suggestion = parseModelConfigResource(config);
    if (!suggestion) return;
    applyModelResourceSuggestionToForm(form, suggestion);
  } catch {
    // 全流程失败静默，由用户在节点步自行配置资源
  }
}
